import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { uniqBy, flatten, orderBy } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import { Modal, Button, FormGroup, Input, Label } from "../components/Modal";
import * as AccountStore from "../account/AccountStore";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import LoadingSpinner from "../general/Loader";
import * as IdentityPermissionStore from "../identityPermissions/IdentityPermissionStore";
import OrganizationContextSwitcher from "../organizations/ContextSwitcher";
import { MembershipType } from "../organizations/OrganizationStore";
import { IdentityUser, SearchUsers } from "../users/UserStore";
import {
    AddChild,
    AddMember,
    CreateChildGroupRequest,
    CreateGroup,
    CreateGroupMemberRequest,
    CreateGroupRequest,
    CreateGroupType,
    CreateGroupTypeRequest,
    DeleteGroup,
    GetGroup,
    GetGroupCustomTypes,
    GetGroups,
    GetOrganizationMembers,
    GetOrgChart,
    Group,
    GroupCustomType,
    GroupType,
    GroupTypeMap,
    GroupUserMembership,
    ImportGroupsFromExcel,
    ImportGroupsFromExcelTestRun,
    OrgChartNode,
    RemoveChild,
    RemoveMember,
    RemoveAllMembers,
    UpdateGroup,
} from "./GroupStore";

import { GroupTree } from "./GroupTree";
import OrgChart from "./OrgChart";
import ScrollContainer from "../components/ScrollContainer";
import { DynamicGroupBuilder } from "./DynamicGroupBuilder";
import { ImportGroupMembersModal } from "./ImportGroupMembers";

let contextSubscriptionId = "";

export const GroupsIndex = () => {
    const [groups, setGroups] = React.useState([] as Group[]);
    const [filteredGroups, setFilteredGroups] = React.useState([] as Group[]);
    const [pages, setPages] = React.useState(0);
    const pageSize = 10;
    const [page, setPage] = React.useState(0);

    const [groupCustomTypes, setGroupCustomTypes] = React.useState([] as GroupCustomType[]);
    const [groupTypeMap, setGroupTypeMap] = React.useState({} as any);

    // Debounced search for smoother typing
    const [searchQuery, setSearchQuery] = React.useState("");
    const [rawSearch, setRawSearch] = React.useState("1");
    const [selectedGroup, setSelectedGroup] = React.useState({} as Group);

    const [organizationId, setOrganizationId] = React.useState("");

    const [selectedGroupMembers, setSelectedGroupMembers] = React.useState([] as GroupUserMembership[]);
    const [filteredSelectedGroupMembers, setFilteredSelectedGroupMembers] = React.useState([] as GroupUserMembership[]);
    const [currentGroupUserFilterQuery, setCurrentGroupUserFilterQuery] = React.useState("");

    const [overview, setOverview] = React.useState(false);
    const [detail, setDetail] = React.useState(true);
    const [orgChart, setOrgChart] = React.useState(false);

    const [loading, setLoading] = React.useState(false);

    // Import states
    const [canImportGroups, setCanImportGroups] = React.useState(false);
    const [showImportGroupsModal, setShowImportGroupsModal] = React.useState(false);
    const [groupFileUploaded, setGroupFileUploaded] = React.useState(false);
    const [groupFileName, setGroupFileName] = React.useState("");
    const [excelFileData, setExcelFileData] = React.useState("");
    const [importGroups, setImportGroups] = React.useState([] as any[]);
    const [enableConfirmImport, setEnableConfirmImport] = React.useState(false);
    const [uploadError, setUploadError] = React.useState("");
    const [importError, setImportError] = React.useState("");
    const [importPage, setImportPage] = React.useState(0);

    const hideTabs = () => {
        setOverview(false);
        setDetail(false);
        setOrgChart(false);
    };

    const [totalMemberships, setTotalMemberships] = React.useState(0);
    const [totalUsers, setTotalUsers] = React.useState(0);

    React.useEffect(() => {
        contextSubscriptionId = GetContext((context) => {
            setOrganizationId(context.currentOrganization.id);
        });
        return () => {
            UnsubscribeContext(contextSubscriptionId);
        };
    }, []);

    // Check import permission
    React.useEffect(() => {
        if (organizationId) {
            IdentityPermissionStore.CheckCurrentUserHasPermission("manageOrganizationGroups").then((result) => {
                setCanImportGroups(result);
            });
        }
    }, [organizationId]);

    // Only fetch group types on org change, not groups
    React.useEffect(() => {
        if (organizationId) {
            getGroupCustomTypes();
        }
    }, [organizationId]);

    // Track pending search criteria
    const [pendingGroupTypeMap, setPendingGroupTypeMap] = React.useState({} as any);
    const [pendingRawSearch, setPendingRawSearch] = React.useState("");
    // On org change, reset pending filters to default
    React.useEffect(() => {
        setPendingGroupTypeMap({});
        setPendingRawSearch("");
    }, [organizationId]);

    const [orgchart, setOrgchart] = React.useState([] as OrgChartNode[]);

    const getGroups = async (groupTypeMap) => {
        setLoading(true);
        let types = Object.keys(groupTypeMap).filter((key) => groupTypeMap[key]);

        let res = await Promise.all([
            GetGroups(organizationId, types.join(","))
                .then((result) => {
                    setGroups(result);
                })
                .catch((response) => {}),
            GetOrgChart(organizationId, types.join(",")).then((res) => {
                setOrgchart(res);
            }),
        ]);

        setLoading(false);

        return res;
    };

    const getGroupCustomTypes = () => {
        GetGroupCustomTypes(organizationId)
            .then((groupTypes) => {
                setGroupCustomTypes(groupTypes);
            })
            .catch((response) => {});
    };

    // Only filter after search is confirmed
    React.useEffect(() => {
        const types = Object.keys(groupTypeMap).filter((key) => groupTypeMap[key]);
        const q = searchQuery.trim().toLowerCase();
        const filtered = groups.filter((group) => {
            const nameMatch = q.length === 0 || group.name.toLowerCase().includes(q) || group.data?.description?.toLowerCase()?.includes(q);
            const typeMatch = types.length === 0 || types.includes(group.groupCustomType?.id);
            return nameMatch && typeMatch;
        });
        setFilteredGroups(filtered);
        setPages(Math.ceil(filtered.length / pageSize));
        setPage(0);
    }, [searchQuery, groups, groupTypeMap]);

    // Only fetch groups when user clicks Search
    const handleSearch = React.useCallback(() => {
        setRawSearch(pendingRawSearch);
        setGroupTypeMap(pendingGroupTypeMap);
        if (organizationId) {
            getGroups(pendingGroupTypeMap);
        }
    }, [pendingRawSearch, pendingGroupTypeMap, organizationId]);

    React.useEffect(() => {
        setFilteredSelectedGroupMembers(selectedGroupMembers.filter((m) => m.member?.firstName?.toLowerCase()?.includes(currentGroupUserFilterQuery.toLowerCase())));
    }, [currentGroupUserFilterQuery, selectedGroupMembers]);

    React.useEffect(() => {
        setTotalMemberships(filteredGroups.reduce((a, b) => a + b.groupUsers.length, 0));
        setTotalUsers(uniqBy(flatten(filteredGroups.map((g) => g.groupUsers)), (g) => g.memberID).length);
    }, [filteredGroups]);

    const handleQueryChange = (value) => {
        setPendingRawSearch(value);
        setSearchQuery(value);
    };

    // Type filter helpers
    const typeCounts = React.useMemo(() => {
        const map = {} as Record<string, number>;
        groups.forEach((g) => {
            const id = g.groupCustomType?.id;
            if (!id) return;
            map[id] = (map[id] || 0) + 1;
        });
        return map;
    }, [groups]);

    const selectAllTypes = () => {
        const next: any = {};
        groupCustomTypes.forEach((t) => (next[t.id] = true));
        setPendingGroupTypeMap(next);
    };
    const clearAllTypes = () => setPendingGroupTypeMap({});
    const resetFilters = () => {
        setPendingRawSearch("");
        setRawSearch("");
        setSearchQuery("");
        setPendingGroupTypeMap({});
        setGroupTypeMap({});
    };

    // Import functions
    const toggleImportGroupsModal = () => {
        setImportError("");
        setUploadError("");
        setImportGroups([]);
        setEnableConfirmImport(false);
        setShowImportGroupsModal(!showImportGroupsModal);
    };

    const onGroupFilesChange = (files: any) => {
        setEnableConfirmImport(false);
        setImportGroups([]);
        setGroupFileUploaded(false);
        if (!files[0]?.name?.toLowerCase().endsWith(".xlsx") || files[0].size > 10 * 1024 * 1024) {
            setUploadError("Choose an Excel file (.xlsx) no larger than 10MB.");
            return;
        }
        setUploadError("");
        var reader = new FileReader();

        reader.onload = (evt) => {
            let result = evt.target?.result?.toString();
            if (result) {
                setExcelFileData(result.substring(result.indexOf("base64,") + 7));
                setGroupFileName(files[0].name);
                setGroupFileUploaded(true);
            }
        };

        if (files.length > 0 && files[0]) {
            reader.readAsDataURL(files[0]);
        }
    };

    const onGroupFilesError = (error: any, file: any) => {
        console.log("error code " + error.code + ": " + error.message);
    };

    const uploadGroups = () => {
        setImportGroups([]);
        ImportGroupsFromExcelTestRun(organizationId, excelFileData)
            .then((response) => {
                setUploadError(response.data.success ? "" : response.data.message || "Resolve the errors before confirming.");
                setImportPage(0);
                setImportGroups(response.data.groups || []);
                setEnableConfirmImport(response.data.success);
            })
            .catch((request) => {
                setUploadError(request.response?.data?.message || "Could not read the group import file.");
            });
    };

    const doImportGroups = () => {
        ImportGroupsFromExcel(organizationId, excelFileData)
            .then((response) => {
                if (!response.data.success) {
                    setImportError(response.data.message || "Could not import groups.");
                    setImportGroups(response.data.groups || []);
                    setEnableConfirmImport(false);
                    return;
                }
                setShowImportGroupsModal(false);
                setSearchQuery("");
                setEnableConfirmImport(false);
                setExcelFileData("");
                setGroupFileName("");
                setGroupFileUploaded(false);
                setImportGroups([]);
                setUploadError("");
                setImportError("");
                getGroups(groupTypeMap);
            })
            .catch((request) => {
                setImportError(request.response?.data?.message || "Could not import groups.");
            });
    };

    const [showGroupMembersModal, setShowGroupMembersModal] = React.useState(false);
    const [loadingGroupMembers, setLoadingGroupMembers] = React.useState(false);
    const [showRemoveAllMembersModal, setShowRemoveAllMembersModal] = React.useState(false);
    const [removingAllMembers, setRemovingAllMembers] = React.useState(false);
    const [removeAllMembersError, setRemoveAllMembersError] = React.useState("");
    const [showImportMembersModal, setShowImportMembersModal] = React.useState(false);
    const [removeMembersMode, setRemoveMembersMode] = React.useState(false);
    const [membershipMessage, setMembershipMessage] = React.useState("");

    const [showChildGroupModal, setShowChildGroupModal] = React.useState(false);

    const startShowGroupMembers = (group: Group) => () => {
        setLoadingGroupMembers(true);
        setSelectedGroup(group);
        setShowGroupMembersModal(true);
        setCurrentGroupUserFilterQuery("");
        setMembershipMessage("");
        GetGroup(organizationId, group.id).then((res) => {
            setSelectedGroup(res);
            setSelectedGroupMembers(orderBy(res.allUsers, ["membershipType", "member.lastName"], ["desc", "asc"]));
            setLoadingGroupMembers(false);
        });
    };

    const [showAddGroupMemberModal, setShowAddGroupMemberModal] = React.useState(false);
    const [showAddGroupOwnerModal, setShowAddGroupOwnerModal] = React.useState(false);
    const [showEditGroupModal, setShowEditGroupModal] = React.useState(false);
    const [editGroupName, setEditGroupName] = React.useState("");
    const [editGroupDescription, setEditGroupDescription] = React.useState("");
    const [isUpdatingGroup, setIsUpdatingGroup] = React.useState(false);

    const startEditGroup = (group: Group) => () => {
        setSelectedGroup(group);
        setEditGroupName(group.name);
        setEditGroupDescription(group.data?.description || "");
        setShowEditGroupModal(true);
    };

    const handleUpdateGroup = async () => {
        setIsUpdatingGroup(true);
        try {
            await UpdateGroup(organizationId, selectedGroup.id, {
                name: editGroupName,
                description: editGroupDescription,
            });
            setShowEditGroupModal(false);
            getGroups(groupTypeMap); // Refresh the groups list
        } catch (error) {
            console.error("Error updating group:", error);
        } finally {
            setIsUpdatingGroup(false);
        }
    };

    const startAddGroupMember = () => {
        setUserSearchDelegated(false);
        setShowGroupMembersModal(false);
        setShowAddGroupMemberModal(true);
        resetUserSearch();
    };

    const startAddGroupOwner = () => {
        setUserSearchDelegated(false);
        setShowGroupMembersModal(false);
        setShowAddGroupOwnerModal(true);
        resetUserSearch();
    };

    const startAddChildGroup = () => {
        setShowGroupMembersModal(false);
        setShowChildGroupModal(true);
    };

    const [userSearchQuery, setUserSearchQuery] = React.useState("");
    const [userSearchResults, setUserSearchResults] = React.useState([] as IdentityUser[]);
    const [userSkip, setUserSkip] = React.useState(0);
    const [userSearchLoading, setUserSearchLoading] = React.useState(false);
    const [userSearchDelegated, setUserSearchDelegated] = React.useState(false);
    const [userSearchTotal, setUserSearchTotal] = React.useState(0);
    const [userSearchLimit] = React.useState(10);

    const [groupSearchQuery, setGroupSearchQuery] = React.useState("");
    const [groupSearchResults, setGroupSearchResults] = React.useState([] as Group[]);
    const [groupSkip, setGroupSkip] = React.useState(0);
    const [groupSearchLoading, setGroupSearchLoading] = React.useState(false);

    const searchUsers = (skipOverride?: number) => {
        const currentSkip = skipOverride !== undefined ? skipOverride : userSkip;
        setUserSearchLoading(true);
        SearchUsers(userSearchQuery, currentSkip, userSearchLimit, true, true, true, true, userSearchDelegated)
            .then((res) => {
                setUserSearchResults(res.data.members || res.data);
                setUserSearchTotal(res.data.total || res.data.length);
                setUserSearchLoading(false);
                if (skipOverride !== undefined) {
                    setUserSkip(currentSkip);
                }
            })
            .catch(() => {
                setUserSearchLoading(false);
            });
    };

    const handleSearchUsers = () => {
        resetUserSearch();
        searchUsers(0);
    };

    const nextUserPage = () => {
        const newSkip = userSkip + userSearchLimit;
        if (newSkip < userSearchTotal) {
            searchUsers(newSkip);
        }
    };

    const prevUserPage = () => {
        const newSkip = Math.max(0, userSkip - userSearchLimit);
        searchUsers(newSkip);
    };

    const resetUserSearch = () => {
        setUserSkip(0);
        setUserSearchResults([]);
        setUserSearchTotal(0);
    };

    const searchGroups = () => {
        setGroupSearchLoading(true);
        GetGroups(organizationId, "", groupSearchQuery).then((res) => {
            setGroupSearchResults(res);
            setGroupSearchLoading(false);
        });
    };

    const addUserToGroup = (user: IdentityUser, role: string) => () => {
        AddMember(organizationId, selectedGroup.id, { userID: user.id, type: role == "member" ? 0 : 1 } as CreateGroupMemberRequest).then((res) => {
            setShowAddGroupMemberModal(false);
            setShowAddGroupOwnerModal(false);
            startShowGroupMembers(selectedGroup)();
            getGroups(groupTypeMap);
        });
    };

    const removeUserFromGroup = (user: IdentityUser) => () => {
        RemoveMember(organizationId, selectedGroup.id, user.id).then((res) => {
            startShowGroupMembers(selectedGroup)();
            getGroups(groupTypeMap);
        });
    };

    const removeAllMembers = async () => {
        setRemovingAllMembers(true);
        setRemoveAllMembersError("");
        try {
            const response = await RemoveAllMembers(organizationId, selectedGroup.id);
            if (!response.success) {
                setRemoveAllMembersError(response.message || "Could not remove members.");
                return;
            }
            setShowRemoveAllMembersModal(false);
            startShowGroupMembers(selectedGroup)();
            setMembershipMessage("All direct members and owners were removed. Child-group memberships are unchanged.");
            getGroups(groupTypeMap);
        } catch (error) {
            setRemoveAllMembersError(error.response?.data?.message || "Could not remove members. Please try again.");
        } finally {
            setRemovingAllMembers(false);
        }
    };

    const addChildGroupToGroup = (parent: Group, child: Group) => {
        AddChild(organizationId, parent.id, { groupID: child.id } as CreateChildGroupRequest).then((res) => {
            setShowChildGroupModal(false);
            startShowGroupMembers(selectedGroup)();
            getGroups(groupTypeMap);
        });
    };

    const removeChildFromGroup = (parent: Group, child: Group) => () => {
        RemoveChild(organizationId, parent.id, child.id).then((res) => {
            startShowGroupMembers(selectedGroup)();
            getGroups(groupTypeMap);
        });
    };

    const [showAddGroupModal, setShowAddGroupModal] = React.useState(false);
    const [groupName, setGroupName] = React.useState("");
    const [groupCustomTypeId, setGroupCustomTypeId] = React.useState("");
    const [groupType, setGroupType] = React.useState(0 as GroupType);

    const startAddGroup = () => {
        setShowAddGroupModal(true);
        setGroupName("");
        setGroupCustomTypeId("");
        setGroupType(0);
    };

    const [showAddGroupTypeModal, setShowAddGroupTypeModal] = React.useState(false);
    const [groupTypeName, setGroupTypeName] = React.useState("");
    const [showDynamicGroupBuilder, setShowDynamicGroupBuilder] = React.useState(false);

    const startAddGroupType = () => {
        setShowAddGroupTypeModal(true);
        setGroupTypeName("");
    };

    const createGroup = () => {
        CreateGroup(organizationId, { name: groupName, typeID: groupCustomTypeId } as CreateGroupRequest).then((res) => {
            if (res.success) {
                getGroups(groupTypeMap);
                setShowAddGroupModal(false);
            }
        });
    };

    const createGroupType = () => {
        CreateGroupType(organizationId, { name: groupTypeName } as CreateGroupTypeRequest).then((res) => {
            if (res.success) {
                getGroupCustomTypes();
                setShowAddGroupTypeModal(false);
            }
        });
    };

    const [showDeleteGroupModal, setShowDeleteGroupModal] = React.useState(false);
    const [groupToDelete, setGroupToDelete] = React.useState({} as Group);

    const startDeleteGroup = (group: Group) => () => {
        setGroupToDelete(group);
        setShowDeleteGroupModal(true);
    };

    const deleteGroup = () => {
        DeleteGroup(groupToDelete.id).then((res) => {
            getGroups(groupTypeMap);
            setShowDeleteGroupModal(false);
        });
    };

    return (
        <div className="space-y-6">
            <OrganizationContextSwitcher />

            {/* Group type filter bar */}
            {groupCustomTypes.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                    <button
                        onClick={selectAllTypes}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <FontAwesomeIcon icon={["fad", "check-double"]} /> All
                    </button>
                    <button
                        onClick={clearAllTypes}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <FontAwesomeIcon icon={["fad", "ban"]} /> None
                    </button>
                    {groupCustomTypes.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setPendingGroupTypeMap({ ...pendingGroupTypeMap, [t.id]: !pendingGroupTypeMap[t.id] })}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                                pendingGroupTypeMap[t.id] ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                            {t.name}
                            {!t.isDynamic && <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">{t.groupCount || 0}</span>}
                        </button>
                    ))}
                </div>
            )}

            {/* Search and Actions Bar */}
            <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={["fad", "magnifying-glass"]} className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Search groups by name or description"
                            value={pendingRawSearch}
                            onChange={(e) => handleQueryChange(e.target.value)}
                        />
                        {pendingRawSearch && (
                            <button onClick={() => handleQueryChange("")} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
                                <FontAwesomeIcon icon={["fad", "xmark"]} />
                            </button>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end">
                        <Button
                            variant="primary"
                            onClick={handleSearch}
                            className={`${pendingRawSearch !== rawSearch || JSON.stringify(pendingGroupTypeMap) !== JSON.stringify(groupTypeMap) ? "ring-2 ring-orange-400 border-orange-400" : ""}`}
                        >
                            <FontAwesomeIcon icon={["fad", "search"]} className="mr-2" /> Search
                        </Button>
                        <Button variant="outline-secondary" onClick={resetFilters}>
                            <FontAwesomeIcon icon={["fad", "rotate-left"]} className="mr-2" /> Reset
                        </Button>
                        <Button variant="outline-primary" onClick={startAddGroup}>
                            <FontAwesomeIcon icon={["fad", "plus"]} className="mr-2" /> Add Group
                        </Button>
                        <Button variant="outline-primary" onClick={startAddGroupType}>
                            <FontAwesomeIcon icon={["fad", "plus"]} className="mr-2" /> Add Group Type
                        </Button>
                        <Button variant="outline-primary" onClick={() => setShowDynamicGroupBuilder(true)}>
                            <FontAwesomeIcon icon={["fad", "users-cog"]} className="mr-2" /> Add Dynamic Groups
                        </Button>
                        {canImportGroups && (
                            <Button variant="outline-success" onClick={toggleImportGroupsModal}>
                                <FontAwesomeIcon icon={["fad", "upload"]} className="mr-2" /> Import Groups
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Loading and View Tabs */}
            {loading && <LoadingSpinner loading={loading} />}
            {!loading && (
                <div className="flex justify-center gap-2">
                    <Button
                        variant={overview ? "primary" : "outline-primary"}
                        onClick={() => {
                            hideTabs();
                            setOverview(true);
                        }}
                    >
                        Overview
                    </Button>
                    <Button
                        variant={detail ? "primary" : "outline-primary"}
                        onClick={() => {
                            hideTabs();
                            setDetail(true);
                        }}
                    >
                        Detail
                    </Button>
                    <Button
                        variant={orgChart ? "primary" : "outline-primary"}
                        onClick={() => {
                            hideTabs();
                            setOrgChart(true);
                        }}
                    >
                        Hierarchy
                    </Button>
                </div>
            )}

            {/* Overview Tab */}
            {overview && (
                <>
                    {/* Statistics Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg shadow-sm p-4 flex items-center gap-3 border border-gray-200">
                            <FontAwesomeIcon icon={["fad", "layer-group"]} className="text-2xl text-blue-500" />
                            <div>
                                <div className="text-sm text-gray-500">Groups</div>
                                <div className="text-2xl font-semibold text-gray-900">{filteredGroups.length}</div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm p-4 flex items-center gap-3 border border-gray-200">
                            <FontAwesomeIcon icon={["fad", "user-group"]} className="text-2xl text-green-500" />
                            <div>
                                <div className="text-sm text-gray-500">Total memberships</div>
                                <div className="text-2xl font-semibold text-gray-900">{totalMemberships}</div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm p-4 flex items-center gap-3 border border-gray-200">
                            <FontAwesomeIcon icon={["fad", "users"]} className="text-2xl text-purple-500" />
                            <div>
                                <div className="text-sm text-gray-500">Unique users</div>
                                <div className="text-2xl font-semibold text-gray-900">{totalUsers}</div>
                            </div>
                        </div>
                    </div>

                    {/* Groups Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredGroups.map((group, i) => (
                            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <div className="grid grid-cols-[56px_1fr_auto] items-center gap-3">
                                    <Avatar name={group.name} size="50px" round={true} />
                                    <div>
                                        <div className="font-semibold text-gray-900">{group.name}</div>
                                        <div className="text-sm text-gray-500">{group.data?.description}</div>
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className="inline-block bg-gray-100 text-gray-700 rounded-md px-2 py-1 text-xs font-medium">
                                                {group.type != 1 ? GroupTypeMap[group.type] || "-" : group.groupCustomType?.name || "-"}
                                            </span>
                                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 rounded-md px-2 py-1 text-xs font-medium">
                                                <FontAwesomeIcon icon={["fad", "user-group"]} /> {group.groupUsers.length}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        {group.type !== GroupType.Dynamic && (
                                            <Button variant="outline-primary" onClick={startEditGroup(group)}>
                                                <FontAwesomeIcon icon={["fad", "edit"]} />
                                            </Button>
                                        )}
                                        <Button variant="outline-success" onClick={startShowGroupMembers(group)}>
                                            <FontAwesomeIcon icon={["fad", "users"]} />
                                        </Button>
                                        {group.type !== GroupType.Dynamic && (
                                            <Button variant="outline-danger" onClick={startDeleteGroup(group)}>
                                                <FontAwesomeIcon icon={["fad", "trash"]} />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
            {/* Detail Tab */}
            {detail && (
                <>
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="w-16 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50"></th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">Type</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredGroups.slice(page * 10, (page + 1) * 10).map((group, i) => (
                                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <Avatar name={group.name} size="40px" round={true} />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">
                                                {group.name} {group.data?.description && <span className="text-gray-500">- {group.data.description}</span>}
                                            </div>
                                            <div className="text-sm text-gray-500">{group.id}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <span className="inline-block bg-gray-100 text-gray-700 rounded-md px-2 py-1 text-xs font-medium">{GroupTypeMap[group.type] || "-"}</span>
                                                {(group.type === GroupType.Custom || group.type === GroupType.Dynamic) && (
                                                    <span className="inline-block bg-blue-100 text-blue-700 rounded-md px-2 py-1 text-xs font-medium">{group.groupCustomType?.name || "-"}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                {group.type !== GroupType.Dynamic && (
                                                    <Button variant="outline-primary" onClick={startEditGroup(group)}>
                                                        <FontAwesomeIcon icon={["fad", "edit"]} className="mr-1" /> Edit
                                                    </Button>
                                                )}
                                                <Button variant="outline-success" onClick={startShowGroupMembers(group)}>
                                                    <FontAwesomeIcon icon={["fad", "users"]} className="mr-1" /> Members
                                                </Button>
                                                {group.type !== GroupType.Dynamic && (
                                                    <Button variant="outline-danger" onClick={startDeleteGroup(group)}>
                                                        <FontAwesomeIcon icon={["fad", "trash"]} className="mr-1" /> Delete
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pages > 1 && (
                        <div className="flex justify-center gap-1">
                            {Array.from(Array(pages).keys()).map((i) => (
                                <button
                                    key={i}
                                    onClick={() => setPage(i)}
                                    className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                                        page === i ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                    }`}
                                >
                                    {i + 1}
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}
            {orgChart && (
                <>
                    <OrgChart orgChart={orgchart} groups={groups} />
                </>
            )}

            {/* Group Members Modal */}
            <Modal
                isOpen={showGroupMembersModal}
                onClose={() => setShowGroupMembersModal(false)}
                title={`${selectedGroup.name} Members - ${selectedGroup?.groupCustomType?.name}`}
                icon={["fad", "users"]}
                size="lg"
            >
                <div className="p-6">
                    <Input type="text" placeholder="Search users..." value={currentGroupUserFilterQuery} onChange={(e) => setCurrentGroupUserFilterQuery(e.target.value)} />

                    {canImportGroups && selectedGroup.type !== GroupType.Dynamic && (
                        <div className="flex flex-wrap gap-2 mt-4">
                            <Button variant="outline-primary" disabled={loadingGroupMembers} onClick={() => {
                                setShowGroupMembersModal(false);
                                setRemoveMembersMode(false);
                                setShowImportMembersModal(true);
                            }}>Import Members</Button>
                            <Button variant="outline-danger" disabled={loadingGroupMembers || !selectedGroup.groupUsers?.length} onClick={() => {
                                setShowGroupMembersModal(false);
                                setRemoveMembersMode(true);
                                setShowImportMembersModal(true);
                            }}>Remove Members</Button>
                            <Button variant="outline-danger" disabled={loadingGroupMembers || !selectedGroup.groupUsers?.length} onClick={() => {
                                setRemoveAllMembersError("");
                                setShowGroupMembersModal(false);
                                setShowRemoveAllMembersModal(true);
                            }}>Remove All Members</Button>
                        </div>
                    )}
                    {membershipMessage && <p role="status" className="mt-4 text-sm text-green-700">{membershipMessage}</p>}

                    <ScrollContainer direction={"-y"} style={{ maxHeight: "80vh" }}>
                        {loadingGroupMembers && <LoadingSpinner />}
                        {filteredSelectedGroupMembers.map((membership) => (
                            <div key={membership.member.id} className="flex justify-between items-center py-3 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <Avatar name={membership.member.firstName} size="50px" round={true} />
                                    <div>
                                        <div className="font-medium text-gray-900">
                                            {membership.member.firstName} {membership.member.lastName}
                                            {membership.membershipType == 1 && <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Owner</span>}
                                        </div>
                                        <div className="text-sm text-gray-500">{membership.member.email}</div>
                                        {selectedGroup.groupUsers.filter((u) => u.memberID == membership.memberID).length == 0 && (
                                            <div className="text-xs text-blue-600">
                                                Member of: {selectedGroup.childGroups.filter((c) => c.groupUsers.some((s) => s.memberID == membership.memberID))[0]?.name}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    {selectedGroup.type != 5 && selectedGroup.groupUsers.filter((u) => u.memberID == membership.memberID).length > 0 && (
                                        <Button variant="outline-danger" onClick={removeUserFromGroup(membership.member)}>
                                            Remove
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {selectedGroup.childGroups?.map((group) => (
                            <div key={group.id} className="flex justify-between items-center py-3 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <Avatar name={group.name} size="50px" round={true} />
                                    <div>
                                        <div className="font-medium text-gray-900">{group.name}</div>
                                        <div className="text-sm text-gray-500">Group • {group.groupCustomType?.name}</div>
                                    </div>
                                </div>
                                <div>
                                    <Button variant="outline-danger" onClick={removeChildFromGroup(selectedGroup, group)}>
                                        Remove
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </ScrollContainer>
                </div>

                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-between">
                    <Button variant="secondary" onClick={() => setShowGroupMembersModal(false)}>
                        Close
                    </Button>
                    {selectedGroup.type != 5 && (
                        <div className="flex gap-2">
                            <Button variant="outline-primary" onClick={() => startAddChildGroup()}>
                                Add Group
                            </Button>
                            <Button variant="outline-primary" onClick={() => startAddGroupMember()}>
                                Add Member
                            </Button>
                            <Button variant="outline-success" onClick={() => startAddGroupOwner()}>
                                Add Owner
                            </Button>
                        </div>
                    )}
                </div>
            </Modal>

            <ImportGroupMembersModal
                isOpen={showImportMembersModal}
                organizationId={organizationId}
                group={selectedGroup}
                remove={removeMembersMode}
                onClose={() => { setShowImportMembersModal(false); setShowGroupMembersModal(true); }}
                onImported={addedCount => {
                    setShowImportMembersModal(false);
                    startShowGroupMembers(selectedGroup)();
                    setMembershipMessage(removeMembersMode ? `${addedCount} direct memberships removed. Child-group memberships were preserved.` : `${addedCount} members imported. Existing memberships and owner roles were preserved.`);
                    getGroups(groupTypeMap);
                }}
            />

            <Modal
                isOpen={showRemoveAllMembersModal}
                onClose={() => {
                    if (!removingAllMembers) { setShowRemoveAllMembersModal(false); setShowGroupMembersModal(true); }
                }}
                title={`Remove All Members from ${selectedGroup.name}`}
                icon={["fad", "trash"]}
                closeOnOverlayClick={!removingAllMembers}
            >
                <div className="p-6 space-y-4">
                    <p className="text-gray-700">Remove all <strong>{selectedGroup.groupUsers?.length || 0}</strong> direct members, including owners, from <strong>{selectedGroup.name}</strong>?</p>
                    <p className="text-sm text-gray-600">The group and user accounts will be kept. Child groups and their members will remain linked. This applies to all direct members, regardless of the search filter.</p>
                    {selectedGroup.source && <p className="text-sm text-gray-600">Members managed by an integration may be added again during the next sync.</p>}
                    {removeAllMembersError && <p role="alert" className="text-sm text-red-700">{removeAllMembersError}</p>}
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end gap-3">
                    <Button variant="secondary" disabled={removingAllMembers} onClick={() => { setShowRemoveAllMembersModal(false); setShowGroupMembersModal(true); }}>Cancel</Button>
                    <Button variant="danger" disabled={removingAllMembers} onClick={removeAllMembers}>{removingAllMembers ? "Removing..." : "Remove All Members"}</Button>
                </div>
            </Modal>

            {/* Add Group Member Modal */}
            <Modal
                isOpen={showAddGroupMemberModal}
                onClose={() => setShowAddGroupMemberModal(false)}
                title={`Add Member to ${selectedGroup.name}`}
                icon={["fad", "user-plus"]}
                size="lg"
            >
                <div className="p-6">
                    <div className="relative mb-4">
                        <input
                            type="text"
                            className="w-full pl-4 pr-20 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Search name, email, user ID or SourceID (student number)..."
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearchUsers()}
                        />
                        <Button
                            variant="outline-secondary"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2"
                            onClick={handleSearchUsers}
                        >
                            Search
                        </Button>
                    </div>
                    
                    <label className="flex items-center gap-2 mb-4 text-sm text-gray-700">
                        <input type="checkbox" role="switch" checked={userSearchDelegated} onChange={e => { setUserSearchDelegated(e.target.checked); resetUserSearch(); }} />
                        Include delegated users (linked parents)
                    </label>
                    <p className="mb-4 text-xs text-gray-600">Search a student number to find the student and, with delegation enabled, their linked parents. Use Import Members to add all resolved users together.</p>
                    {userSearchTotal > 0 && (
                        <div className="flex justify-between items-center mb-4 text-sm text-gray-600">
                            <span>
                                Showing {userSkip + 1}-{Math.min(userSkip + userSearchLimit, userSearchTotal)} of {userSearchTotal} users
                            </span>
                            <div className="flex gap-1">
                                <Button size="sm" variant="outline-secondary" disabled={userSkip === 0} onClick={prevUserPage}>
                                    <FontAwesomeIcon icon={["fad", "chevron-left"]} />
                                </Button>
                                <Button size="sm" variant="outline-secondary" disabled={userSkip + userSearchLimit >= userSearchTotal} onClick={nextUserPage}>
                                    <FontAwesomeIcon icon={["fad", "chevron-right"]} />
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    <ScrollContainer direction={"-y"} style={{ maxHeight: "60vh" }}>
                        {userSearchLoading && <LoadingSpinner />}
                        {userSearchResults.map((user) => (
                            <div key={user.id} className="flex justify-between items-center py-3 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <Avatar name={user.firstName} size="50px" round={true} />
                                    <div>
                                        <div className="font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                                        <div className="text-sm text-gray-500">{user.email}</div>
                                    </div>
                                </div>
                                <Button variant="outline-primary" onClick={addUserToGroup(user, "member")}>
                                    Add
                                </Button>
                            </div>
                        ))}
                    </ScrollContainer>
                </div>
                
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end">
                    <Button variant="secondary" onClick={() => setShowAddGroupMemberModal(false)}>
                        Close
                    </Button>
                </div>
            </Modal>

            {/* Add Group Owner Modal */}
            <Modal
                isOpen={showAddGroupOwnerModal}
                onClose={() => setShowAddGroupOwnerModal(false)}
                title={`Add Owner to ${selectedGroup.name}`}
                icon={["fad", "user-crown"]}
                size="lg"
            >
                <div className="p-6">
                    <div className="relative mb-4">
                        <input
                            type="text" 
                            className="w-full pl-4 pr-20 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Search for users..."
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearchUsers()}
                        />
                        <Button
                            variant="outline-secondary"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2"
                            onClick={handleSearchUsers}
                        >
                            Search
                        </Button>
                    </div>
                    
                    {userSearchTotal > 0 && (
                        <div className="flex justify-between items-center mb-4 text-sm text-gray-600">
                            <span>
                                Showing {userSkip + 1}-{Math.min(userSkip + userSearchLimit, userSearchTotal)} of {userSearchTotal} users
                            </span>
                            <div className="flex gap-1">
                                <Button size="sm" variant="outline-secondary" disabled={userSkip === 0} onClick={prevUserPage}>
                                    <FontAwesomeIcon icon={["fad", "chevron-left"]} />
                                </Button>
                                <Button size="sm" variant="outline-secondary" disabled={userSkip + userSearchLimit >= userSearchTotal} onClick={nextUserPage}>
                                    <FontAwesomeIcon icon={["fad", "chevron-right"]} />
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    <ScrollContainer direction={"-y"} style={{ maxHeight: "60vh" }}>
                        {userSearchLoading && <LoadingSpinner />}
                        {userSearchResults.map((user) => (
                            <div key={user.id} className="flex justify-between items-center py-3 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <Avatar name={user.firstName} size="50px" round={true} />
                                    <div>
                                        <div className="font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                                        <div className="text-sm text-gray-500">{user.email}</div>
                                    </div>
                                </div>
                                <Button variant="outline-success" onClick={addUserToGroup(user, "owner")}>
                                    Add as Owner
                                </Button>
                            </div>
                        ))}
                    </ScrollContainer>
                </div>
                
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end">
                    <Button variant="secondary" onClick={() => setShowAddGroupOwnerModal(false)}>
                        Close
                    </Button>
                </div>
            </Modal>

            {/* Add Child Group Modal */}
            <Modal
                isOpen={showChildGroupModal}
                onClose={() => setShowChildGroupModal(false)}
                title={`Add Group to ${selectedGroup.name}`}
                icon={["fad", "layer-group"]}
                size="lg"
            >
                <div className="p-6">
                    <div className="relative mb-4">
                        <input
                            type="text"
                            className="w-full pl-4 pr-20 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Search for groups..."
                            value={groupSearchQuery}
                            onChange={(e) => setGroupSearchQuery(e.target.value)}
                        />
                        <Button
                            variant="outline-secondary"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2"
                            onClick={searchGroups}
                        >
                            Search
                        </Button>
                    </div>
                    
                    <ScrollContainer direction={"-y"} style={{ maxHeight: "60vh" }}>
                        {groupSearchLoading && <LoadingSpinner />}
                        {groupSearchResults.map((group) => (
                            <div key={group.id} className="flex justify-between items-center py-3 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <Avatar name={group.name} size="50px" round={true} />
                                    <div>
                                        <div className="font-medium text-gray-900">{group.name}</div>
                                        <div className="text-sm text-gray-500">{group.groupCustomType?.name}</div>
                                    </div>
                                </div>
                                <Button variant="outline-primary" onClick={() => addChildGroupToGroup(selectedGroup, group)}>
                                    Add
                                </Button>
                            </div>
                        ))}
                    </ScrollContainer>
                </div>
                
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end">
                    <Button variant="secondary" onClick={() => setShowChildGroupModal(false)}>
                        Close
                    </Button>
                </div>
            </Modal>

            {/* Add Group Modal */}
            <DynamicGroupBuilder
                organizationId={organizationId}
                isOpen={showDynamicGroupBuilder}
                onClose={() => setShowDynamicGroupBuilder(false)}
                onCreated={() => {
                    getGroupCustomTypes();
                    getGroups(groupTypeMap);
                }}
            />

            <Modal
                isOpen={showAddGroupModal}
                onClose={() => setShowAddGroupModal(false)}
                title="Add Group"
                icon={["fad", "plus-circle"]}
            >
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Group Name</label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            placeholder="Enter group name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Group Type</label>
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={groupCustomTypeId}
                            onChange={(e) => setGroupCustomTypeId(e.target.value)}
                        >
                            <option value="">Select Group Type</option>
                            {groupCustomTypes.map((groupCustomType) => (
                                <option key={groupCustomType.id} value={groupCustomType.id}>
                                    {groupCustomType.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end gap-3">
                    <Button variant="secondary" onClick={() => setShowAddGroupModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={createGroup} disabled={!groupName.trim()}>
                        Create Group
                    </Button>
                </div>
            </Modal>

            {/* Add Group Type Modal */}
            <Modal
                isOpen={showAddGroupTypeModal}
                onClose={() => setShowAddGroupTypeModal(false)}
                title="Add Group Type"
                icon={["fad", "tags"]}
            >
                <div className="p-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Group Type Name</label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={groupTypeName}
                            onChange={(e) => setGroupTypeName(e.target.value)}
                            placeholder="Enter group type name"
                        />
                    </div>
                </div>
                
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end gap-3">
                    <Button variant="secondary" onClick={() => setShowAddGroupTypeModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={createGroupType} disabled={!groupTypeName.trim()}>
                        Create Group Type
                    </Button>
                </div>
            </Modal>

            {/* Delete Group Modal */}
            <Modal
                isOpen={showDeleteGroupModal}
                onClose={() => setShowDeleteGroupModal(false)}
                title={`Delete ${groupToDelete.name}`}
                icon={["fad", "trash-can"]}
            >
                <div className="p-6">
                    <div className="flex items-center gap-3 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-red-600 text-xl" />
                        <div>
                            <div className="font-medium text-red-800">This action cannot be undone</div>
                            <div className="text-sm text-red-600">All members will be removed from this group</div>
                        </div>
                    </div>
                    <p className="text-gray-700">
                        Are you sure you want to delete the group <strong>{groupToDelete.name}</strong>?
                    </p>
                </div>
                
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end gap-3">
                    <Button variant="secondary" onClick={() => setShowDeleteGroupModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={deleteGroup}>
                        Delete Group
                    </Button>
                </div>
            </Modal>

            {/* Edit Group Modal */}
            <Modal
                isOpen={showEditGroupModal}
                onClose={() => setShowEditGroupModal(false)}
                title={`Edit Group - ${selectedGroup.name}`}
                icon={["fad", "edit"]}
            >
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Group Name</label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={editGroupName}
                            onChange={(e) => setEditGroupName(e.target.value)}
                            placeholder="Enter group name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={editGroupDescription}
                            onChange={(e) => setEditGroupDescription(e.target.value)}
                            placeholder="Enter group description"
                            rows={3}
                        />
                    </div>
                </div>
                
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end gap-3">
                    <Button variant="secondary" onClick={() => setShowEditGroupModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdateGroup} disabled={isUpdatingGroup || !editGroupName.trim()}>
                        {isUpdatingGroup ? "Updating..." : "Update Group"}
                    </Button>
                </div>
            </Modal>

            {/* Import Groups Modal */}
            <Modal
                isOpen={showImportGroupsModal}
                onClose={toggleImportGroupsModal}
                title="Import Groups"
                icon={["fad", "upload"]}
                size="lg"
            >
                <div className="p-6">
                    {importGroups.length === 0 && (
                        <div className="space-y-4">
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="text-sm text-blue-800">
                                    Upload an Excel file (.xlsx) to create or update group details. This does not import members.
                                    Identify existing groups by GroupID or SourceID (and Source when needed). Without an ID, a unique group name is used.
                                    To import members into an existing group, open its Members dialog and choose Import Members.
                                </div>
                            </div>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer">
                                <input
                                    type="file"
                                    accept=".xlsx"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files.length > 0) {
                                            onGroupFilesChange([e.target.files[0]]);
                                        }
                                    }}
                                    className="hidden"
                                    id="groupFileInput"
                                />
                                <label htmlFor="groupFileInput" className="cursor-pointer">
                                    <FontAwesomeIcon icon={["fad", "cloud-upload"]} className="text-4xl text-gray-400 mb-3" />
                                    <div className="text-lg font-medium text-gray-600 mb-1">
                                        {groupFileUploaded ? groupFileName : "Drop file here or click to upload"}
                                    </div>
                                    <div className="text-sm text-gray-500">Excel files (.xlsx) only, max 10MB</div>
                                </label>
                            </div>
                        </div>
                    )}
                    
                    {uploadError && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                            <div className="text-sm text-red-800">{uploadError}</div>
                        </div>
                    )}

                    {importGroups.length > 0 && (
                        <div className="space-y-4">
                            <div className="bg-white shadow overflow-hidden rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group Type</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SourceID</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {importGroups
                                            .filter((_, i) => i >= 25 * importPage && i < 25 * (importPage + 1))
                                            .map((groupImport, i) => (
                                                <tr key={i} className="hover:bg-gray-50">
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{groupImport.groupName}</td>
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{groupImport.groupType || "Default"}</td>
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{groupImport.description || "-"}</td>
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{groupImport.sourceID || "-"}</td>
                                                    <td className="px-4 py-4 whitespace-nowrap">
                                                        {groupImport.isNew && <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">New</span>}
                                                        {groupImport.isUpdate && <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Updated</span>}
                                                        {groupImport.hasErrors && <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Error</span>}
                                                        {groupImport.errors?.map((error, errorIndex) => <div key={errorIndex} className="mt-1 text-sm text-red-700">{error}</div>)}
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            {importGroups.length > 25 && (
                                <div className="flex justify-between items-center">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setImportPage(Math.max(importPage - 1, 0))}
                                        disabled={importPage === 0}
                                    >
                                        Previous Page
                                    </Button>
                                    <span className="text-sm text-gray-600">
                                        Page {importPage + 1} of {Math.ceil(importGroups.length / 25)}
                                    </span>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setImportPage(Math.min(importPage + 1, Math.floor(importGroups.length / 25)))}
                                        disabled={importPage >= Math.floor(importGroups.length / 25)}
                                    >
                                        Next Page
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {importError && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mt-4">
                            <div className="text-sm text-red-800">{importError}</div>
                        </div>
                    )}
                </div>
                
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-between items-center">
                    <a
                        href="api/integrations/downloadGroupExcelImportTemplate"
                        download="Group Import Template.xlsx"
                        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
                    >
                        <FontAwesomeIcon icon={["fad", "download"]} className="mr-1" />
                        Download Template
                    </a>
                    <div className="flex gap-3">
                        <Button variant="secondary" onClick={toggleImportGroupsModal}>
                            Cancel
                        </Button>
                        {groupFileUploaded && !enableConfirmImport && (
                            <Button variant="outline-primary" onClick={uploadGroups}>
                                Next
                            </Button>
                        )}
                        {enableConfirmImport && (
                            <Button variant="primary" onClick={doImportGroups}>
                                Confirm Import
                            </Button>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default GroupsIndex;
